import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TextInput,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useAuth } from '../context/AuthContext';
import { useNotifications, SignalRMessage } from '../context/NotificationContext';
import { useClassTeachers } from '../hooks/useClassTeachers';
import { chatAPI } from '../services/api';
import { normalizeToUtcIso } from '../utils/dateUtils';
import { Logger } from '../utils/logger';
import { refreshInboxUnread } from '../hooks/useInboxUnread';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Toast } from '../components/common/Toast';
import { WebPageShell } from '../components/common/WebPageShell';
import { useTheme, ThemeColors } from '../context/ThemeContext';

interface ChatMessageItem {
  id: string;
  isMine: boolean;
  text: string;
  time: string;
  createdDate: string;
  status?: 'sent' | 'error';
}

const normalizeId = (value?: string | null): string => (value || '').toLowerCase();

// normalizeToUtcIso now lives in utils/dateUtils, shared with every other screen
// that renders a backend timestamp.

const webInputStyle = Platform.OS === 'web' ? ({ outlineStyle: 'none', resize: 'none' } as any) : null;

const formatTime = (iso: string): string => {
  try {
    const date = new Date(normalizeToUtcIso(iso));
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
};

export function ChatScreen({ isNested }: { isNested?: boolean } = {}) {
  const { selectedStudent } = useAuth();
  const { subscribe, unsubscribe } = useNotifications();
  const { teachers, classTeacher, loading: teachersLoading } = useClassTeachers(selectedStudent?.classId);
  const { colors, isDark } = useTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  // Defaults to the class teacher, but a parent can switch to any subject teacher
  // of the class via the picker row below — each is its own 1:1 thread against the
  // same chat.Message table, not a group chat (the backend has no group chat yet).
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);

  useEffect(() => {
    if (teachers.length === 0) {
      setSelectedTeacherId(null);
      return;
    }
    if (selectedTeacherId && teachers.some((t) => t.id === selectedTeacherId)) return;
    setSelectedTeacherId(classTeacher?.id ?? teachers[0].id);
  }, [teachers, classTeacher, selectedTeacherId]);

  const teacher = teachers.find((t) => t.id === selectedTeacherId) ?? null;

  const [messages, setMessages] = useState<ChatMessageItem[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState<{ visible: boolean; message: string; type: 'success' | 'error' | 'info' }>({
    visible: false,
    message: '',
    type: 'error',
  });
  const flatRef = useRef<FlatList>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'error') => {
    setToast({ visible: true, message, type });
  };

  const scrollSoon = (animated = true) => {
    setTimeout(() => flatRef.current?.scrollToEnd({ animated }), 100);
  };

  useEffect(() => {
    if (!teacher) {
      setMessages([]);
      return;
    }

    let isMounted = true;

    const loadHistory = async () => {
      setLoadingMessages(true);
      setLoadError(null);

      try {
        const { data } = await chatAPI.getMessages(teacher.id);
        if (!isMounted) return;

        const mapped: ChatMessageItem[] = (data ?? [])
          .map((m: any) => ({
            id: m.id,
            isMine: normalizeId(m.senderUserId) !== normalizeId(teacher.id),
            text: m.messageText,
            time: formatTime(m.createdDate),
            createdDate: m.createdDate,
            status: 'sent' as const,
          }))
          .sort(
            (a: ChatMessageItem, b: ChatMessageItem) =>
              new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
          );

        setMessages(mapped);
        scrollSoon(false);

        // Read on the server -> the Inbox badges drop (hooks/useInboxUnread).
        chatAPI.markRead(teacher.id).then(refreshInboxUnread).catch((err) => {
          Logger.warn('Chat', 'Failed to mark messages as read', err);
        });
      } catch (err) {
        Logger.error('Chat', 'Failed to load chat history', err);
        if (isMounted) setLoadError('Failed to load messages');
      } finally {
        if (isMounted) setLoadingMessages(false);
      }
    };

    loadHistory();

    return () => {
      isMounted = false;
    };
  }, [teacher?.id]);

  useEffect(() => {
    if (!teacher) return;

    const handleIncoming = (msg: SignalRMessage) => {
      if (msg.eventType !== 'Chat') return;

      const payload = msg.payload || {};
      const id = payload.id || payload.Id;
      const senderId = payload.senderUserId || payload.SenderUserId;
      const receiverId = payload.receiverUserId || payload.ReceiverUserId;
      const messageText = payload.messageText || payload.MessageText;
      const createdDate = payload.createdDate || payload.CreatedDate || new Date().toISOString();

      if (!id || !senderId || !receiverId || !messageText) return;

      const isRelevant =
        normalizeId(senderId) === normalizeId(teacher.id) ||
        normalizeId(receiverId) === normalizeId(teacher.id);
      if (!isRelevant) return;

      const incomingFromTeacher = normalizeId(senderId) === normalizeId(teacher.id);

      setMessages((prev) => {
        const tempMatch = prev.find(
          (m) => m.id.startsWith('temp-') && !incomingFromTeacher && m.text === messageText,
        );

        if (tempMatch) {
          return prev.map((m) =>
            m.id === tempMatch.id
              ? { id, isMine: true, text: messageText, time: formatTime(createdDate), createdDate, status: 'sent' as const }
              : m,
          );
        }

        if (prev.some((m) => normalizeId(m.id) === normalizeId(id))) {
          return prev;
        }

        return [
          ...prev,
          {
            id,
            isMine: !incomingFromTeacher,
            text: messageText,
            time: formatTime(createdDate),
            createdDate,
            status: 'sent' as const,
          },
        ];
      });

      if (incomingFromTeacher) {
        chatAPI.markRead(teacher.id).then(refreshInboxUnread).catch(() => { });
      }

      scrollSoon();
    };

    subscribe('Chat', handleIncoming);
    return () => unsubscribe('Chat', handleIncoming);
  }, [teacher?.id, subscribe, unsubscribe]);

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed || !teacher || sending) return;

    const optimisticId = `temp-${Date.now()}`;
    const now = new Date().toISOString();

    setMessages((prev) => [
      ...prev,
      {
        id: optimisticId,
        isMine: true,
        text: trimmed,
        time: formatTime(now),
        createdDate: now,
        status: 'sent',
      },
    ]);
    setText('');
    setSending(true);
    scrollSoon();

    try {
      await chatAPI.sendMessage(teacher.id, trimmed);
    } catch (err) {
      Logger.error('Chat', 'Failed to send message', err);
      setMessages((prev) =>
        prev.map((m) => (m.id === optimisticId ? { ...m, status: 'error' } : m)),
      );
      showToast('Message failed to send', 'error');
    } finally {
      setSending(false);
    }
  };

  const renderItem = ({ item }: { item: ChatMessageItem }) => (
    <View style={[styles.bubbleRow, item.isMine ? styles.bubbleRowRight : styles.bubbleRowLeft]}>
      <View style={[styles.bubble, isMobile && styles.bubbleMobile, item.isMine ? styles.bubbleParent : styles.bubbleTeacher]}>
        <Text style={[styles.bubbleText, item.isMine ? styles.bubbleTextParent : styles.bubbleTextTeacher]}>
          {item.text}
        </Text>
        <View style={styles.bubbleMetaRow}>
          <Text style={[styles.bubbleTime, item.isMine ? styles.bubbleTimeParent : styles.bubbleTimeTeacher]}>
            {item.time}
          </Text>
          {item.status === 'error' && (
            <FeatherIcon name="alert-circle" size={12} color="#FCA5A5" style={styles.errorIcon} />
          )}
        </View>
      </View>
    </View>
  );

  const initials = (teacher?.name || '?').trim().charAt(0).toUpperCase();

  const content = (
    <KeyboardAvoidingView
      style={[styles.container, isNested && { backgroundColor: 'transparent' }]}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      {!selectedStudent ? (
        <View style={styles.centerState}>
          <Text style={styles.centerStateText}>Select a child to start chatting with their teachers.</Text>
        </View>
      ) : teachersLoading ? (
        <LoadingSpinner message="Loading teachers..." />
      ) : teachers.length === 0 ? (
        <View style={styles.centerState}>
          <Text style={styles.centerStateText}>
            No teachers assigned yet for {selectedStudent.class || 'this class'}.
          </Text>
        </View>
      ) : (
        <>
          {teachers.length > 1 && (
            // Wraps onto more rows rather than one long scrolling row: the row cut the last
            // teachers off with nothing to show there were more - often the selected one
            // (bug 17435). Every teacher is now on screen, full name, selection visible.
            <View style={styles.teacherPickerRow} accessibilityRole="tablist">
              {teachers.map((t) => {
                const isSelected = t.id === selectedTeacherId;
                const chipInitials = (t.name || '?').trim().charAt(0).toUpperCase();
                return (
                  <TouchableOpacity
                    key={t.id}
                    style={[styles.teacherChip, isSelected && styles.teacherChipSelected]}
                    onPress={() => setSelectedTeacherId(t.id)}
                    accessibilityRole="tab"
                    accessibilityState={{ selected: isSelected }}
                    accessibilityLabel={`${t.name}${t.isClassTeacher ? ', class teacher' : ''}`}
                  >
                    <View style={[styles.teacherChipAvatar, isSelected && styles.teacherChipAvatarSelected]}>
                      <Text style={[styles.teacherChipAvatarText, isSelected && styles.teacherChipAvatarTextSelected]}>
                        {chipInitials}
                      </Text>
                      {t.unreadCount > 0 && <View style={styles.teacherChipUnreadDot} />}
                    </View>
                    <Text
                      style={[styles.teacherChipName, isSelected && styles.teacherChipNameSelected]}
                      numberOfLines={1}
                    >
                      {t.name}
                    </Text>
                    {/* A star rather than a "Class Teacher" caption: the header card
                        below already names the selected teacher's role in full, and the
                        caption forced every pill tall enough to wrap. */}
                    {t.isClassTeacher && (
                      <FeatherIcon
                        name="star"
                        size={11}
                        color={isSelected ? '#FFFFFF' : (isDark ? colors.primary : '#1D4ED8')}
                      />
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          {!teacher ? (
            <View style={styles.centerState}>
              <Text style={styles.centerStateText}>Select a teacher above to start chatting.</Text>
            </View>
          ) : (
          <>
          <View style={[styles.headerCard, isMobile && styles.headerCardMobile]}>
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>{initials}</Text>
            </View>
            <View style={styles.headerText}>
              <Text style={styles.headerName} numberOfLines={1}>{teacher.name}</Text>
              <Text style={styles.headerSubText} numberOfLines={2}>
                {[teacher.isClassTeacher ? 'Class Teacher' : null, teacher.subjects.join(', ') || null]
                  .filter(Boolean).join(' · ') || 'Teacher'}
              </Text>
            </View>
          </View>

          {loadingMessages ? (
            <LoadingSpinner message="Loading messages..." />
          ) : (
            <FlatList
              ref={flatRef}
              data={messages}
              keyExtractor={(m) => m.id}
              renderItem={renderItem}
              scrollEnabled={!isNested}
              contentContainerStyle={[styles.messageList, isMobile && styles.messageListMobile]}
              onLayout={() => flatRef.current?.scrollToEnd({ animated: false })}
              ListEmptyComponent={
                <View style={styles.centerState}>
                  <Text style={styles.centerStateText}>
                    No messages yet. Say hello to {teacher.name.split(' ')[0]}!
                  </Text>
                </View>
              }
            />
          )}

          {loadError && <Text style={styles.errorText}>{loadError}</Text>}

          <View style={[styles.inputBar, isMobile && styles.inputBarMobile]}>
            <TextInput
              style={[styles.input, webInputStyle]}
              placeholder={`Message ${teacher.name}...`}
              placeholderTextColor={colors.textMuted}
              value={text}
              onChangeText={setText}
              maxLength={500}
              blurOnSubmit={false}
              onKeyPress={(e) => {
                if (Platform.OS !== 'web') return;
                const nativeEvent = e.nativeEvent as unknown as { key: string; shiftKey?: boolean; preventDefault?: () => void };
                if (nativeEvent.key === 'Enter' && !nativeEvent.shiftKey) {
                  nativeEvent.preventDefault?.();
                  send();
                }
              }}
            />
            <TouchableOpacity
              style={[styles.sendBtn, (!text.trim() || sending) && styles.sendBtnDisabled]}
              onPress={send}
              disabled={!text.trim() || sending}
            >
              {sending ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <FeatherIcon name="send" size={18} color="#FFFFFF" />
              )}
            </TouchableOpacity>
          </View>
          </>
          )}
        </>
      )}

      <Toast
        visible={toast.visible}
        message={toast.message}
        type={toast.type}
        onDismiss={() => setToast((t) => ({ ...t, visible: false }))}
      />
    </KeyboardAvoidingView>
  );

  if (isNested) return content;
  return <WebPageShell title="Chat">{content}</WebPageShell>;
}

const createStyles = (colors: ThemeColors, isDark: boolean) => StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: colors.background,
  },
  centerState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  centerStateText: {
    fontSize: 14,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
  },
  errorText: {
    color: '#DC2626',
    fontSize: 12,
    textAlign: 'center',
    paddingBottom: 8,
  },
  teacherPickerRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    flexShrink: 0,
    paddingHorizontal: 20,
    paddingTop: 4,
    paddingBottom: 12,
    gap: 8,
    alignItems: 'center',
  },
  // A horizontal pill — initial, first name, optional star on one line — matching the
  // Chat/Events/Gallery filter chips this row sits directly beneath. The previous
  // vertical stack (avatar over name over caption) was tall enough to be clipped.
  teacherChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingLeft: 6,
    paddingRight: 14,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.border,
  } as any,
  teacherChipSelected: {
    backgroundColor: isDark ? colors.primary : '#1D4ED8',
    borderColor: isDark ? colors.primary : '#1D4ED8',
  },
  teacherChipAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  } as any,
  teacherChipAvatarSelected: {
    backgroundColor: 'rgba(255,255,255,0.25)',
  },
  teacherChipAvatarText: {
    fontSize: 13,
    fontWeight: '800',
    color: isDark ? colors.primary : '#1D4ED8',
  },
  teacherChipAvatarTextSelected: {
    color: '#FFFFFF',
  },
  teacherChipUnreadDot: {
    position: 'absolute',
    top: -1,
    right: -1,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#EF4444',
    borderWidth: 2,
    borderColor: colors.card,
  } as any,
  teacherChipName: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    maxWidth: 200,
  },
  teacherChipNameSelected: {
    color: '#FFFFFF',
    fontWeight: '800',
  },
  headerCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.card,
    marginHorizontal: 20,
    marginTop: 8,
    marginBottom: 12,
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.divider,
  },
  headerCardMobile: {
    marginHorizontal: 12,
    padding: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  avatarText: {
    fontSize: 18,
    fontWeight: '800',
    color: colors.primary,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  headerName: {
    fontSize: 16,
    fontWeight: '800',
    color: colors.textPrimary,
    marginBottom: 2,
  },
  headerSubText: {
    fontSize: 13,
    color: colors.textSecondary,
    fontWeight: '600',
  },
  messageList: {
    flexGrow: 1,
    paddingHorizontal: 20,
    paddingBottom: 16,
  },
  messageListMobile: {
    paddingHorizontal: 12,
  },
  bubbleRow: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  bubbleRowLeft: {
    justifyContent: 'flex-start',
  },
  bubbleRowRight: {
    justifyContent: 'flex-end',
  },
  bubble: {
    maxWidth: '85%',
    borderRadius: 16,
    padding: 16,
  },
  bubbleMobile: {
    maxWidth: '92%',
    padding: 12,
  },
  bubbleParent: {
    backgroundColor: isDark ? colors.primary : '#1D4ED8',
    borderBottomRightRadius: 4,
  },
  bubbleTeacher: {
    backgroundColor: colors.card,
    borderBottomLeftRadius: 4,
    borderWidth: 1,
    borderColor: colors.divider,
  },
  bubbleText: {
    fontSize: 15,
    lineHeight: 22,
    fontWeight: '500',
  },
  bubbleTextParent: {
    color: '#FFFFFF',
  },
  bubbleTextTeacher: {
    color: colors.textPrimary,
  },
  bubbleMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  bubbleTime: {
    fontSize: 11,
    fontWeight: '600',
  },
  bubbleTimeParent: {
    color: 'rgba(255,255,255,0.7)',
  },
  bubbleTimeTeacher: {
    color: colors.textMuted,
  },
  errorIcon: {
    marginLeft: 6,
  },
  inputBar: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 20,
    paddingVertical: 6,
    backgroundColor: colors.background,
    gap: 12,
  },
  inputBarMobile: {
    paddingHorizontal: 12,
    gap: 8,
  },
  input: {
    flex: 1,
    minHeight: 48,
    maxHeight: 100,
    backgroundColor: colors.card,
    borderRadius: 24,
    paddingHorizontal: 20,
    paddingTop: 14,
    paddingBottom: 14,
    fontSize: 15,
    lineHeight: 20,
    color: colors.textPrimary,
    borderWidth: 1,
    borderColor: colors.border,
    textAlignVertical: 'center',
  },
  sendBtn: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#EA580C',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendBtnDisabled: {
    opacity: 0.5,
  },
});
